// reminders.ts — an on-device daily nudge to write. Ported from oriora_plant_diary/src/reminders.ts
// (the proven fleet pattern) and reshaped: Plant Diary schedules one reminder per row; a journal
// wants a single recurring time of day.
//
// No server, no push service, no APNs — the OS fires it locally. Nothing about the reminder, the
// time, or the journal leaves the device. Best-effort by design: a denied permission, the web target
// (no plugin), or any plugin error must never block the app.
import {
  isPermissionGranted,
  requestPermission,
  sendNotification,
  cancelAll,
  Schedule,
} from "@tauri-apps/plugin-notification";

const KEY = "oriora.journal.reminder";
/** How many days ahead to lay down. The plugin schedules one-shot notifications, so we place a
 *  fortnight and re-lay them every launch — which the app does anyway on any settings change. */
const DAYS_AHEAD = 14;

export interface ReminderSetting {
  enabled: boolean;
  /** "HH:MM", 24h, local time. */
  time: string;
}

export const DEFAULT_REMINDER: ReminderSetting = { enabled: false, time: "21:00" };

export function loadReminder(): ReminderSetting {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_REMINDER };
    const v = JSON.parse(raw) as Partial<ReminderSetting>;
    const time = typeof v.time === "string" && /^\d{2}:\d{2}$/.test(v.time) ? v.time : DEFAULT_REMINDER.time;
    return { enabled: !!v.enabled, time };
  } catch {
    return { ...DEFAULT_REMINDER };
  }
}

export function saveReminder(s: ReminderSetting): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode — the reminder just won't persist */
  }
}

// Deterministic 32-bit id per day (iOS notification ids must be i32).
function idFor(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (Math.imul(h, 31) + key.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

/** A gentle, factual line — never a guilt trip, and never anything about what they wrote. */
const LINES = [
  "A minute to write?",
  "Anything worth keeping from today?",
  "Your journal is here when you want it.",
];

/**
 * Clear any previously-scheduled nudges and lay down the next fortnight, or clear them entirely
 * when the reminder is switched off. Idempotent — safe to call on every launch and every change.
 * Returns true if reminders are actually scheduled (permission granted and enabled).
 */
export async function syncReminder(setting: ReminderSetting): Promise<boolean> {
  try {
    if (!setting.enabled) {
      try { await cancelAll(); } catch { /* nothing was scheduled */ }
      return false;
    }
    let ok = await isPermissionGranted();
    if (!ok) ok = (await requestPermission()) === "granted";
    if (!ok) return false;

    const [hh, mm] = setting.time.split(":").map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;

    await cancelAll(); // drop the old set, then re-lay fresh
    const now = new Date();
    for (let d = 0; d < DAYS_AHEAD; d++) {
      const at = new Date(now);
      at.setDate(at.getDate() + d);
      at.setHours(hh, mm, 0, 0);
      if (at.getTime() <= Date.now()) continue; // today's slot already passed
      sendNotification({
        id: idFor(`journal-${at.toISOString().slice(0, 10)}`),
        title: "Journal",
        body: LINES[d % LINES.length],
        schedule: Schedule.at(at),
      });
    }
    return true;
  } catch {
    /* a nicety — never let it throw into the UI */
    return false;
  }
}
